import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, ReactNode } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { getExpressShippingDiscountPercent, applyShippingDiscount } from '../services/shippingService';

export interface ShippingTier {
  id: string;
  name: string;
  description: string;
  cost: number;
  jmPrice?: number | null;
  freeShippingThreshold?: number;
  deliveryTimeMin: number;
  deliveryTimeMax: number;
  processingTimeMin: number;
  processingTimeMax: number;
  active: boolean;
  sortOrder: number;
  maxWeight?: number;
  zones?: string[];
  // Territory restrictions
  excludedCountries?: string[]; // Countries where this tier is NOT available
  includedCountries?: string[]; // Countries where this tier IS available (takes precedence)
}

export interface ShippingConfiguration {
  id: string;
  tiers: ShippingTier[];
  lastUpdated: Date;
  version: string;
  expressShippingDiscountPercent: number;
}

interface ShippingContextType {
  shippingConfig: ShippingConfiguration | null;
  loading: boolean;
  error: string | null;
  getActiveShippingTiers: () => ShippingTier[];
  getAllActiveShippingTiers: () => ShippingTier[]; // Returns all active tiers regardless of country
  getAvailableShippingTiers: (country?: string) => ShippingTier[];
  isTierAvailableInCountry: (tier: ShippingTier, country?: string) => boolean; // Check if tier is available in country
  getProductShippingTiers: (availableShippingOptions?: string[]) => ShippingTier[];
  getShippingTierById: (tierId: string) => ShippingTier | null;
  calculateShippingCost: (tier: ShippingTier, cartTotal: number) => number;
  // Express rate functionality (provider-aware)
  dhlExpressRate: number | null;
  loadingDhlRate: boolean;
  dhlRateError: string | null;
  rateProvider: string | null;
  fallbackUsed: boolean;
  fetchDhlExpressRate: (cartItems: any[], shippingAddress: any) => Promise<void>;
}

const ShippingContext = createContext<ShippingContextType | undefined>(undefined);

// Default shipping configuration (fallback)
const DEFAULT_SHIPPING_CONFIG: ShippingConfiguration = {
  id: 'default',
  tiers: [
    {
      id: 'standard',
      name: 'Standard Shipping',
      description: 'Reliable delivery within business days',
      cost: 12.95,
      jmPrice: null,
      freeShippingThreshold: 85.00,
      deliveryTimeMin: 8,
      deliveryTimeMax: 12,
      processingTimeMin: 1,
      processingTimeMax: 2,
      active: true,
      sortOrder: 1,
      maxWeight: 150
    },
    {
      id: 'express',
      name: 'Express Shipping',
      description: 'Faster delivery for urgent orders',
      cost: 35.00,
      jmPrice: null,
      deliveryTimeMin: 3,
      deliveryTimeMax: 5,
      processingTimeMin: 1,
      processingTimeMax: 1,
      active: true,
      sortOrder: 2,
      maxWeight: 70
    }
  ],
  lastUpdated: new Date(),
  version: '1.0.0',
  expressShippingDiscountPercent: 0
};

interface ShippingProviderProps {
  children: ReactNode;
}

export function ShippingProvider({ children }: ShippingProviderProps) {
  const [shippingConfig, setShippingConfig] = useState<ShippingConfiguration | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Express rate state (provider-aware)
  const [dhlExpressRate, setDhlExpressRate] = useState<number | null>(null);
  const [loadingDhlRate, setLoadingDhlRate] = useState(false);
  const [dhlRateError, setDhlRateError] = useState<string | null>(null);
  const [rateProvider, setRateProvider] = useState<string | null>(null);
  const [fallbackUsed, setFallbackUsed] = useState(false);
  const [dhlRateFailures, setDhlRateFailures] = useState(0);
  const [currentCountry, setCurrentCountry] = useState<string | null>(null);

  useEffect(() => {
    
    // Set up real-time listener for shipping configuration
    const unsubscribe = onSnapshot(
      doc(db, 'shippingConfig', 'main'),
      (doc) => {
        try {
          if (doc.exists()) {
            const data = doc.data();
            const config: ShippingConfiguration = {
              id: doc.id,
              tiers: data.tiers.map((tier: any) => ({
                ...tier,
                jmPrice: tier.jmPrice ?? null,
                lastUpdated: tier.lastUpdated?.toDate() || new Date()
              })),
              lastUpdated: data.lastUpdated?.toDate() || new Date(),
              version: data.version || '1.0.0',
              expressShippingDiscountPercent: data.expressShippingDiscountPercent || 0
            };
            
            setShippingConfig(config);
            setError(null);
          } else {
            setShippingConfig(DEFAULT_SHIPPING_CONFIG);
            setError(null);
          }
        } catch (err) {
          setError('Failed to load shipping configuration');
          setShippingConfig(DEFAULT_SHIPPING_CONFIG);
        } finally {
          setLoading(false);
        }
      },
      (err) => {
        setError('Failed to connect to shipping configuration');
        setShippingConfig(DEFAULT_SHIPPING_CONFIG);
        setLoading(false);
      }
    );

    // Cleanup listener on unmount
    return () => {
      unsubscribe();
    };
  }, []);

  // Get all active shipping tiers (without territory filtering)
  const getActiveShippingTiers = useCallback((): ShippingTier[] => {
    if (!shippingConfig) return [];
    return shippingConfig.tiers
      .filter(tier => tier.active)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [shippingConfig]);

  // Get all active shipping tiers regardless of country restrictions
  const getAllActiveShippingTiers = useCallback((): ShippingTier[] => {
    if (!shippingConfig) return [];
    return shippingConfig.tiers
      .filter(tier => tier.active)
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [shippingConfig]);

  // Check if a specific tier is available in a country
  const isTierAvailableInCountry = useCallback((tier: ShippingTier, country?: string): boolean => {
    if (!country) return true; // If no country specified, assume available
    
    // Check included countries first (takes precedence)
    if (tier.includedCountries && tier.includedCountries.length > 0) {
      return tier.includedCountries.includes(country);
    }
    
    // Check excluded countries
    if (tier.excludedCountries && tier.excludedCountries.length > 0) {
      return !tier.excludedCountries.includes(country);
    }
    
    return true; // No restrictions, available everywhere
  }, []);

  // Get shipping tiers available for a specific country
  const getAvailableShippingTiers = useCallback((country?: string): ShippingTier[] => {
    if (!shippingConfig) return [];
    
    return shippingConfig.tiers
      .filter(tier => {
        if (!tier.active) return false;
        
        // Territory filtering
        if (country) {
          // Check included countries first (takes precedence)
          if (tier.includedCountries && tier.includedCountries.length > 0) {
            return tier.includedCountries.includes(country);
          }
          
          // Check excluded countries
          if (tier.excludedCountries && tier.excludedCountries.length > 0) {
            return !tier.excludedCountries.includes(country);
          }
        }
        
        return true;
      })
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [shippingConfig]);

  // Get shipping tiers available for a specific product
  const getProductShippingTiers = useCallback((availableShippingOptions?: string[]): ShippingTier[] => {
    if (!shippingConfig) return [];
    
    // If no specific options are assigned, return all active tiers (for backward compatibility)
    if (!availableShippingOptions || availableShippingOptions.length === 0) {
      return getActiveShippingTiers();
    }
    
    // Filter tiers based on product's assigned options
    return shippingConfig.tiers
      .filter(tier => tier.active && availableShippingOptions.includes(tier.id))
      .sort((a, b) => a.sortOrder - b.sortOrder);
  }, [shippingConfig, getActiveShippingTiers]);

  // Get a specific shipping tier by ID
  const getShippingTierById = useCallback((tierId: string): ShippingTier | null => {
    if (!shippingConfig) return null;
    return shippingConfig.tiers.find(tier => tier.id === tierId) || null;
  }, [shippingConfig]);

  // Calculate shipping cost for a given tier and cart total
  const calculateShippingCost = useCallback((tier: ShippingTier, cartTotal: number): number => {
    if (cartTotal === 0) return 0;
    
    // Check if order qualifies for free shipping
    if (tier.freeShippingThreshold && tier.freeShippingThreshold > 0 && cartTotal >= tier.freeShippingThreshold) {
      return 0;
    }
    
    const isJamaica = currentCountry?.toUpperCase() === 'JM';
    if (isJamaica && tier.jmPrice != null) {
      return tier.jmPrice;
    }
    
    return tier.cost;
  }, [currentCountry]);

  // Fetch DHL Express rate from backend
  const fetchDhlExpressRate = useCallback(async (cartItems: any[], shippingAddress: any): Promise<void> => {
    // Circuit breaker: stop trying after 3 consecutive failures
    if (dhlRateFailures >= 3) {
      setDhlRateError('DHL Express rate service temporarily unavailable');
      return;
    }

    // Check if express tier exists first
    const expressTier = getActiveShippingTiers().find(tier => tier.id === 'express');
    if (!expressTier) {
      return;
    }
    
    const country = shippingAddress?.value?.address?.country || shippingAddress?.address?.country;
    setCurrentCountry(country || null);
    
    // If Jamaican address and jmPrice is configured, bypass DHL and use jmPrice
    if (country && country.toUpperCase() === 'JM' && expressTier.jmPrice != null) {
      setDhlExpressRate(expressTier.jmPrice);
      setRateProvider('jm-fixed');
      setFallbackUsed(false);
      setDhlRateError(null);
      setLoadingDhlRate(false);
      return;
    }

    setLoadingDhlRate(true);
    setDhlRateError(null);

    const maxAttempts = 5;
    let lastError: any = null;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        // Get the API base URL from environment or use a default
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001';
        
        const response = await fetch(`${apiBaseUrl}/shipping/express-rate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            cartItems,
            shippingAddress
          })
        });

        const result = await response.json();
        

        if (result.success && result.rate) {
          // Apply express shipping discount if configured
          const discountPercent = await getExpressShippingDiscountPercent();
          const originalRate = result.rate.cost;
          const discountedRate = applyShippingDiscount(originalRate, discountPercent);
          
          setDhlExpressRate(discountedRate);
          setDhlRateError(null);
          setDhlRateFailures(0); // Reset failure count on success
          setRateProvider(result.provider || 'easypost');
          setFallbackUsed(result.fallbackUsed || false);
          setLoadingDhlRate(false);
          
          return; // Success - exit the retry loop
        } else {
          lastError = result.error || 'DHL Express not available';
          
          if (attempt === maxAttempts) {
            // Only set error states after all attempts are exhausted
            setDhlRateError(lastError);
            setDhlExpressRate(null);
            setRateProvider(null);
            setFallbackUsed(false);
            setDhlRateFailures(prev => prev + 1);
          }
        }
      } catch (error) {
        lastError = error;
        
        if (attempt === maxAttempts) {
          // Only set error states after all attempts are exhausted
          setDhlRateError('Failed to get Express shipping rate');
          setDhlExpressRate(null);
          setRateProvider(null);
          setFallbackUsed(false);
          setDhlRateFailures(prev => prev + 1);
        }
      }
    }

    setLoadingDhlRate(false);
  }, [getActiveShippingTiers, dhlRateFailures]);

  const value: ShippingContextType = useMemo(() => ({
    shippingConfig,
    loading,
    error,
    getActiveShippingTiers,
    getAllActiveShippingTiers,
    getAvailableShippingTiers,
    isTierAvailableInCountry,
    getProductShippingTiers,
    getShippingTierById,
    calculateShippingCost,
    // Express rate functionality (provider-aware)
    dhlExpressRate,
    loadingDhlRate,
    dhlRateError,
    rateProvider,
    fallbackUsed,
    fetchDhlExpressRate
  }), [
    shippingConfig,
    loading,
    error,
    dhlExpressRate,
    loadingDhlRate,
    dhlRateError,
    rateProvider,
    fallbackUsed,
    getActiveShippingTiers,
    getAllActiveShippingTiers,
    getAvailableShippingTiers,
    isTierAvailableInCountry,
    getProductShippingTiers,
    getShippingTierById,
    calculateShippingCost,
    fetchDhlExpressRate
  ]);

  return (
    <ShippingContext.Provider value={value}>
      {children}
    </ShippingContext.Provider>
  );
}

export function useShipping() {
  const context = useContext(ShippingContext);
  if (context === undefined) {
    throw new Error('useShipping must be used within a ShippingProvider');
  }
  return context;
} 
